import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './AbilityUsageCell.module.css';
import { AbilityUse } from '../utils/types';
import AbilityUsageMenu from './AbilityUsageMenu';

interface AbilityUsageCellProps {
  roundNumber: number;
  playerIndex: number;
  abilityUsages: AbilityUse[];
  isUltimate?: boolean;
  isDisabled?: boolean;
  onUsageClick?: (roundNumber: number, playerIndex: number, timeOffset: number) => void;
  agent: string;
  abilityIcon: string;
  abilityName: string;
  hasSubscription?: boolean;
}

const AbilityUsageCell: React.FC<AbilityUsageCellProps> = ({
  roundNumber,
  playerIndex,
  abilityUsages,
  isUltimate = false,
  isDisabled = false,
  onUsageClick,
  agent,
  abilityIcon,
  abilityName,
  hasSubscription = false,
}) => {
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);

  if (isDisabled) {
    return <td className={styles.disabledCell}>-</td>;
  }

  // Check if round is locked (round 13+ without subscription)
  const isLocked = roundNumber >= 13 && !hasSubscription;

  if (isLocked) {
    return (
      <td className={`${styles.cell} ${styles.lockedCell}`}>
        <i className="bi bi-lock"></i>
      </td>
    );
  }

  const count = abilityUsages.length;
  
  const handleClick = (event: React.MouseEvent) => {
    if (count === 0 || !onUsageClick) return;

    if (count === 1) {
      // If there's only one usage, play it directly
      const timeOffset = Math.floor(abilityUsages[0].timeSeconds) - 1.25;
      onUsageClick(roundNumber, playerIndex, timeOffset);
    } else {
      // If there are multiple usages, show the menu
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuPosition({
        x: rect.left,
        y: rect.bottom + window.scrollY,
      });
    }
  };

  const handleMenuSelect = (timeOffset: number) => {
    if (onUsageClick) {
      onUsageClick(roundNumber, playerIndex, timeOffset);
    }
    setMenuPosition(null);
  };

  return (
    <>
      <td 
        className={`${styles.cell} ${count === 0 ? styles.zeroCount : styles.countCell} ${isUltimate ? styles.ultimateCell : ''}`}
        data-count={count > 5 ? "5" : count.toString()}
        onClick={handleClick}
        style={{ cursor: count > 0 ? 'pointer' : 'default' }}
      >
        {count}
      </td>
      {menuPosition && count > 1 && createPortal(
        <AbilityUsageMenu
          abilityUsages={abilityUsages}
          position={menuPosition}
          onSelect={handleMenuSelect}
          onClose={() => setMenuPosition(null)}
          agent={agent}
          abilityIcon={abilityIcon}
          abilityName={abilityName}
          roundNumber={roundNumber}
        />,
        document.body
      )}
    </>
  );
};

export default AbilityUsageCell; 